import { useCookieConsent } from './useCookieConsent'
import { getBrowserSessionStorage, getCurrentJourneyId, readVisitorJourney, snapshotVisitorJourney } from '../utils/visitorJourney'

type PendingSignalsEvent = {
  eventName: string
  properties: Record<string, any>
  requiresAnalyticsConsent: boolean
}

type SignalsKitCommerceItem = {
  id: string
  name: string
  price: number
  quantity: number
  brand?: string
  sku?: string
  category?: string
  type?: string
  variant?: string
  size?: string
  color?: string
}

type SignalsKitPrivateAccessRequest = {
  eventId?: string
  intentContext?: string
  pagePath?: string
  productType: string
  productName: string
  productColor: string
  size?: string
  sourceCategory?: string
  locale: string
  optIn: boolean
  value?: number
  currency?: string
}

type SignalsKitFrictionKind =
  | 'application_error'
  | 'browser_responsiveness'
  | 'conversion_abandonment'
  | 'form_validation'
  | 'guardrail_block'
  | 'none'
  | 'request_failure'

type SignalsKitFrictionSeverity = 'none' | 'info' | 'warning' | 'error'
type SignalsKitProblemKind =
  | 'application_error'
  | 'data_quality'
  | 'degraded_fallback'
  | 'guardrail_block'
  | 'integration_error'
  | 'none'
  | 'performance'

type SignalsKitFrictionOptions = {
  kind?: SignalsKitFrictionKind
  problemKind?: SignalsKitProblemKind
  severity?: SignalsKitFrictionSeverity
}

type SignalsKitFormGoalOptions = {
  eventName?: 'form_submit' | 'contact_request' | 'preorder_submit'
  formType: string
  formLocation: string
  requestType?: string
  locale?: string
  result?: 'success' | 'error' | 'attempt'
  leadId?: string | number | null
  properties?: Record<string, any>
}

const pendingSignalsEvents: PendingSignalsEvent[] = []
let readyListenerAttached = false
let flushTimeoutId: number | null = null
let flushRetryCount = 0
const MAX_FLUSH_RETRIES = 20
const MAX_PENDING_SIGNALS_EVENTS = 100
const EDITORIAL_SIGNAL_EVENTS = new Set([
  'founder_story_scroll_depth',
  'founder_story_reading_time',
  'founder_story_instagram_follow_click',
  'magazine_scroll_depth',
  'magazine_continue_click',
  'magazine_product_link_click',
  'magazine_article_link_click',
  'product_link_click',
  'site_navigation_click',
  'instagram_follow_cta_view',
  'instagram_follow_cta_click',
  'product_card_view',
  'product_card_click',
  'private_access_cta_view',
  'private_access_cta_click',
  'private_access_form_arrival',
  'private_access_form_view',
  'private_access_form_start',
  'private_access_form_focus',
  'private_access_form_field_change',
  'private_access_form_submit_attempt',
  'private_access_form_submit_success',
  'private_access_form_submit_error',
  'b2b_lead_form_submit_attempt',
  'b2b_lead_form_submit_error',
  'contact_request',
  'form_submit',
  'preorder_submit',
  'kendrick_application_error_failed',
  'kendrick_browser_responsiveness_failed',
  'kendrick_private_access_form_abandoned',
  'kendrick_private_access_request_failed',
  'kendrick_private_access_validation_blocked',
  'social_click'
])

const toSignalsKitCommerceItem = (item: SignalsKitCommerceItem) => ({
  item_id: item.id,
  item_name: item.name,
  item_brand: item.brand || 'Kendrick Paris',
  sku: item.sku || item.id,
  item_category: item.category || item.type || 'unknown',
  item_variant: item.variant || item.size || 'not_selected',
  item_variant2: item.color || 'not_selected',
  price: item.price,
  quantity: item.quantity
})

const compactSignalsPayload = <T extends Record<string, any>>(payload: T): Partial<T> => {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null)
  ) as Partial<T>
}

const resolveFrictionMetadata = (
  eventName: string,
  options: SignalsKitFrictionOptions = {}
) => {
  const resolveProblemKind = (kind: SignalsKitFrictionKind): SignalsKitProblemKind => {
    if (options.problemKind) return options.problemKind
    if (kind === 'application_error' || kind === 'request_failure') return 'application_error'
    if (kind === 'browser_responsiveness') return 'performance'
    if (kind === 'form_validation' || kind === 'guardrail_block') return 'guardrail_block'
    return 'none'
  }

  if (options.kind || options.severity || options.problemKind) {
    const frictionKind = options.kind || 'guardrail_block'
    const severity = options.severity || (frictionKind === 'application_error' || frictionKind === 'request_failure' ? 'error' : 'warning')
    const problemKind = resolveProblemKind(frictionKind)
    return { frictionKind, problemKind, severity }
  }

  if (eventName === 'kendrick_browser_responsiveness_failed') {
    return { frictionKind: 'browser_responsiveness' as const, problemKind: 'performance' as const, severity: 'error' as const }
  }

  if (eventName.endsWith('_failed')) {
    return { frictionKind: 'application_error' as const, problemKind: 'application_error' as const, severity: 'error' as const }
  }

  if (eventName.endsWith('_blocked')) {
    return { frictionKind: 'guardrail_block' as const, problemKind: 'guardrail_block' as const, severity: 'warning' as const }
  }

  if (eventName.endsWith('_abandoned')) {
    return { frictionKind: 'conversion_abandonment' as const, problemKind: 'none' as const, severity: 'warning' as const }
  }

  return { frictionKind: 'none' as const, problemKind: 'none' as const, severity: 'none' as const }
}

/**
 * Session provenance (channel, referrer, UTM, landing page, page sequence)
 * attached to conversion events. Paths carry no query strings.
 */
const getAttributionProperties = (): Record<string, any> => {
  const journey = readVisitorJourney(getBrowserSessionStorage())
  if (!journey) return {}

  const snapshot = snapshotVisitorJourney(journey, Date.now(), typeof navigator !== 'undefined' ? navigator.userAgent : null)
  return compactSignalsPayload({
    traffic_channel: snapshot.channel,
    social_network: snapshot.socialNetwork,
    referrer_host: snapshot.referrerHost,
    referrer: snapshot.referrer,
    utm_source: snapshot.utm?.source,
    utm_medium: snapshot.utm?.medium,
    utm_campaign: snapshot.utm?.campaign,
    utm_term: snapshot.utm?.term,
    utm_content: snapshot.utm?.content,
    click_ids: snapshot.clickIds?.join(','),
    landing_path: snapshot.landingPath,
    session_page_count: snapshot.pageCount,
    session_duration_seconds: snapshot.durationSeconds,
    journey_path_sequence: snapshot.pages.slice(-12).map(page => page.path).join(' > ')
  })
}

const persistEditorialSignal = (eventName: string, properties: Record<string, any>) => {
  if (typeof window === 'undefined') return

  try {
    const body = JSON.stringify({
      eventName,
      properties
    })

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' })
      if (navigator.sendBeacon('/api/editorial-signal', blob)) return
    }

    void fetch('/api/editorial-signal', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true
    }).catch(() => {})
  } catch {
    // Analytics persistence must not affect the page interaction.
  }
}

const hasAnalyticsConsentNow = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    const cmpkit = (window as any).cmpkit
    if (cmpkit && typeof cmpkit.getConsent === 'function') {
      const consent = cmpkit.getConsent()
      if (consent && typeof consent.analytics === 'boolean') {
        return consent.analytics === true
      }
    }

    if (cmpkit && typeof cmpkit.hasConsent === 'function') {
      return cmpkit.hasConsent('analytics') === true
    }
  } catch {
    // Fall back to composable state below.
  }

  try {
    const { getConsent } = useCookieConsent()
    return getConsent().analytics === true
  } catch {
    return false
  }
}

const isSignalsKitLoadedNow = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }

  return typeof (window as any).signalsKit !== 'undefined' &&
    typeof (window as any).signalsKit?.track === 'function'
}

const enqueueSignalsEvent = (
  eventName: string,
  properties: Record<string, any>,
  requiresAnalyticsConsent: boolean
) => {
  if (pendingSignalsEvents.length >= MAX_PENDING_SIGNALS_EVENTS) {
    pendingSignalsEvents.shift()
  }

  pendingSignalsEvents.push({
    eventName,
    properties,
    requiresAnalyticsConsent
  })
}

const flushPendingSignalsEvents = () => {
  if (typeof window === 'undefined') {
    return false
  }

  if (!isSignalsKitLoadedNow()) {
    return false
  }

  const hasAnalyticsConsent = hasAnalyticsConsentNow()
  flushRetryCount = 0

  while (pendingSignalsEvents.length > 0) {
    const nextEvent = pendingSignalsEvents.shift()
    if (!nextEvent) continue
    if (nextEvent.requiresAnalyticsConsent && !hasAnalyticsConsent) continue

    try {
      ;(window as any).signalsKit.track(nextEvent.eventName, nextEvent.properties)
    } catch (error) {
      console.warn('[SignalsKit] Error flushing queued event:', error)
    }
  }

  return true
}

const schedulePendingSignalsFlush = () => {
  if (typeof window === 'undefined') {
    return
  }

  if (flushTimeoutId !== null || flushRetryCount >= MAX_FLUSH_RETRIES) {
    return
  }

  flushTimeoutId = window.setTimeout(() => {
    flushTimeoutId = null

    if (flushPendingSignalsEvents()) {
      return
    }

    flushRetryCount += 1
    schedulePendingSignalsFlush()
  }, 400)
}

const ensureSignalsReadyListener = () => {
  if (readyListenerAttached || typeof window === 'undefined') {
    return
  }

  window.addEventListener('signalskit:ready', () => {
    flushPendingSignalsEvents()
  })
  readyListenerAttached = true
}

/**
 * SignalsKit tracking composable
 * Centralized tracking for SignalsKit events
 * 
 * Documentation: https://signalskit.com/docs
 * Usage: signalsKit.track('event_name', { property: 'value' })
 */

// SignalsKit is loaded by the client plugin only after CMPKit analytics consent.
// It should be available as window.signalsKit after the script loads.

export const useSignalsKit = () => {
  const hasAnalyticsConsent = (): boolean => {
    return hasAnalyticsConsentNow()
  }

  // Check if SignalsKit is loaded
  const isSignalsKitLoaded = (): boolean => {
    return isSignalsKitLoadedNow()
  }

  // Track a custom event
  const trackInternal = (
    eventName: string,
    properties: Record<string, any> = {},
    requiresAnalyticsConsent = true
  ) => {
    ensureSignalsReadyListener()

    if (requiresAnalyticsConsent && !hasAnalyticsConsent()) {
      return
    }

    if (!isSignalsKitLoaded()) {
      enqueueSignalsEvent(
        eventName,
        properties,
        requiresAnalyticsConsent
      )
      schedulePendingSignalsFlush()
      return
    }

    flushPendingSignalsEvents()

    try {
      (window as any).signalsKit.track(eventName, properties)
      console.log('📊 [SignalsKit] Event tracked:', eventName, properties)
    } catch (error) {
      console.warn('[SignalsKit] Error tracking event:', error)
    }
  }

  const track = (eventName: string, properties?: Record<string, any>) => {
    trackInternal(eventName, properties || {}, true)
  }

  const trackEditorialSignal = (eventName: string, properties?: Record<string, any>) => {
    if (!EDITORIAL_SIGNAL_EVENTS.has(eventName)) {
      track(eventName, properties)
      return
    }

    const payload = compactSignalsPayload({
      ...(properties || {}),
      journey_id: getCurrentJourneyId(getBrowserSessionStorage()),
      signalskit_client_available: isSignalsKitLoaded(),
      measurement_basis: hasAnalyticsConsent() ? 'analytics_consent' : 'editorial_measurement',
      measurement_scope: 'anonymous_editorial'
    })

    persistEditorialSignal(eventName, payload)
    trackInternal(eventName, payload, false)
  }

  const trackFormGoal = (options: SignalsKitFormGoalOptions) => {
    const eventName = options.eventName || 'form_submit'
    trackEditorialSignal(eventName, {
      page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      page_url: typeof window !== 'undefined' ? window.location.origin + window.location.pathname : undefined,
      locale: options.locale,
      form_type: options.formType,
      form_location: options.formLocation,
      request_type: options.requestType,
      request_result: options.result || 'success',
      conversion_stage: `${options.formType}_${options.result || 'success'}`,
      business_outcome: options.result === 'error' ? 'form_submission_failed' : 'form_submission_recorded',
      lead_id: options.leadId ?? undefined,
      is_database_recorded: options.result === 'success',
      ...getAttributionProperties(),
      ...(options.properties || {})
    })
  }

  const trackFrictionSignal = (
    eventName: string,
    properties: Record<string, any> = {},
    options: SignalsKitFrictionOptions = {}
  ) => {
    const { frictionKind, problemKind, severity } = resolveFrictionMetadata(eventName, options)
    const problemSignal = problemKind !== 'none'
    const payload = compactSignalsPayload({
      app: 'kendrick',
      page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      friction_signal: true,
      friction_kind: frictionKind,
      friction_severity: severity,
      friction_reason: properties.friction_reason || properties.reason,
      problem_signal: problemSignal,
      problem_kind: problemKind,
      problem_severity: problemSignal ? severity : 'none',
      ...properties
    })

    trackEditorialSignal(eventName, payload)
  }

  // E-commerce events
  // With consent, can include user_id and hashed email for better tracking (RGPD compliant)
  const trackAddToCart = (productId: string, productName: string, price: number, quantity: number = 1, size?: string, color?: string, type?: string, isAuthenticated?: boolean, userId?: string, emailHash?: string) => {
    const total = price * quantity
    const item = toSignalsKitCommerceItem({
      id: productId,
      name: productName,
      price,
      quantity,
      category: type,
      size,
      color
    })
    const eventData: Record<string, any> = {
      product_id: productId,
      product_name: productName,
      sku: productId, // Use product_id as SKU (format: type-color-size)
      price,
      quantity,
      size: size || 'not_selected',
      color: color || 'not_selected',
      product_type: type || 'unknown',
      currency: 'EUR',
      value: total,
      total, // Explicit total field
      items: [item],
      is_authenticated: isAuthenticated ?? false
    }

    // Add user data only if provided (requires explicit consent)
    // RGPD compliant because:
    // 1. Email is hashed (SHA-256) - pseudonymized
    // 2. User ID is Supabase UUID - pseudonymized
    // 3. Only sent with explicit analytics consent
    if (userId) {
      eventData.user_id = userId
    }
    if (emailHash) {
      eventData.email_hash = emailHash
    }

    track('add_to_cart', eventData)
  }

  const trackRemoveFromCart = (productId: string, productName: string, price: number, quantity: number = 1, isAuthenticated?: boolean) => {
    track('remove_from_cart', {
      product_id: productId,
      product_name: productName,
      price,
      quantity,
      currency: 'EUR',
      value: price * quantity,
      is_authenticated: isAuthenticated ?? false
    })
  }

  const trackBeginCheckout = (items: SignalsKitCommerceItem[], totalValue: number, isAuthenticated?: boolean) => {
    // Track with 'begin_checkout' for GA4 compatibility
    track('begin_checkout', {
      items: items.map(toSignalsKitCommerceItem),
      value: totalValue,
      total: totalValue, // Explicit total field
      currency: 'EUR',
      is_authenticated: isAuthenticated ?? false
    })
    // Also track as 'checkout' with step 'started' for SignalsKit funnel
    track('checkout', {
      step: 'started',
      items: items.map(toSignalsKitCommerceItem),
      value: totalValue,
      total: totalValue, // Explicit total field
      currency: 'EUR',
      is_authenticated: isAuthenticated ?? false
    })
  }

  const trackCheckoutInformation = (items: SignalsKitCommerceItem[], totalValue: number, isAuthenticated?: boolean) => {
    // Track as 'checkout' with step 'information' for SignalsKit funnel
    track('checkout', {
      step: 'information',
      items: items.map(toSignalsKitCommerceItem),
      value: totalValue,
      total: totalValue, // Explicit total field
      currency: 'EUR',
      is_authenticated: isAuthenticated ?? false
    })
  }

  const trackPurchase = (
    transactionId: string,
    items: SignalsKitCommerceItem[],
    totalValue: number,
    tax?: number,
    shipping?: number,
    currency: string = 'EUR',
    isAuthenticated?: boolean
  ) => {
    // Track with 'purchase' event name
    track('purchase', {
      transaction_id: transactionId,
      value: totalValue,
      total: totalValue, // Explicit total field
      currency,
      tax: tax || 0,
      shipping: shipping || 0,
      is_authenticated: isAuthenticated ?? false,
      items: items.map(toSignalsKitCommerceItem)
    })
    // Also track as 'checkout' with step 'completed' for SignalsKit funnel
    track('checkout', {
      step: 'completed',
      transaction_id: transactionId,
      value: totalValue,
      total: totalValue, // Explicit total field
      currency,
      tax: tax || 0,
      shipping: shipping || 0,
      is_authenticated: isAuthenticated ?? false,
      items: items.map(toSignalsKitCommerceItem)
    })
  }

  const trackViewItem = (productId: string, productName: string, price: number, category?: string, color?: string, size?: string, isAuthenticated?: boolean) => {
    const item = toSignalsKitCommerceItem({
      id: productId,
      name: productName,
      price,
      quantity: 1,
      category,
      size,
      color
    })
    track('view_item', {
      product_id: productId,
      product_name: productName,
      sku: productId,
      price,
      category: category || 'unknown',
      color: color || 'not_selected',
      size: size || 'not_selected',
      currency: 'EUR',
      value: price,
      items: [item],
      is_authenticated: isAuthenticated ?? false
    })
  }

  const trackViewCart = (items: SignalsKitCommerceItem[], totalValue: number, isAuthenticated?: boolean) => {
    track('view_cart', {
      items: items.map(toSignalsKitCommerceItem),
      value: totalValue,
      total: totalValue,
      currency: 'EUR',
      is_authenticated: isAuthenticated ?? false
    })
  }

  // Product interaction events
  const trackSizeSelection = (productType: string, productColor: string, size: string, inStock: boolean) => {
    track('size_selected', {
      product_type: productType,
      product_color: productColor,
      size,
      in_stock: inStock,
      stock_type: inStock ? 'available' : 'out_of_stock'
    })
  }

  const trackColorSelection = (colorName: string, colorLabel: string, context: string = 'homepage') => {
    track('color_selected', {
      color_name: colorName,
      color_label: colorLabel,
      context,
      interaction_type: 'color_swatch_click'
    })
  }

  const trackSizeGuideOpen = (productType: string, productColor: string) => {
    track('size_guide_open', {
      product_type: productType,
      product_color: productColor,
      interaction_type: 'size_guide'
    })
  }

  const trackProductDetails = (section: 'description' | 'details' | 'care_instructions' | 'returns', productType: string, productColor: string) => {
    track('product_details_view', {
      section,
      product_type: productType,
      product_color: productColor,
      interaction_type: 'details_expand'
    })
  }

  // Engagement events
  const trackNewsletterSubscribe = (context: string, size?: string, source?: string) => {
    track('newsletter_subscribe', {
      context,
      size: size || 'not_selected',
      source: source || 'unknown',
      subscription_type: 'product_drops'
    })
  }

  const trackPrivateAccessRequest = (request: SignalsKitPrivateAccessRequest) => {
    const item = toSignalsKitCommerceItem({
      id: `${request.productType}-${request.productColor}`,
      name: request.productName,
      price: request.value ?? 0,
      quantity: 1,
      category: request.productType,
      size: request.size,
      color: request.productColor
    })

    const properties = compactSignalsPayload({
      event_id: request.eventId,
      lead_source: 'private_access',
      conversion_type: 'private_access',
      interaction_type: 'private_access_request',
      product_type: request.productType,
      product_name: request.productName,
      product_color: request.productColor,
      size: request.size || 'not_selected',
      locale: request.locale,
      marketing_opt_in: request.optIn,
      source_category: request.sourceCategory,
      intent_context: request.intentContext,
      value: request.value ?? 0,
      currency: request.currency || 'EUR',
      items: [item],
      page_path: request.pagePath || (typeof window !== 'undefined' ? window.location.pathname : undefined),
      page_location: typeof window !== 'undefined' ? window.location.origin + window.location.pathname : undefined,
      ...getAttributionProperties()
    })

    track('private_access_request', properties)
    track('generate_lead', properties)
  }

  const trackSocialClick = (platform: 'instagram' | 'facebook' | 'twitter' | 'linkedin' | 'pinterest', location: 'header' | 'footer' | 'mobile_menu') => {
    trackEditorialSignal('social_click', {
      platform,
      social_platform: platform,
      location,
      interaction_type: 'social_link_click',
      page_path: typeof window !== 'undefined' ? window.location.pathname : undefined
    })
  }

  // Store events
  const trackStoreInteraction = (storeName: string, interactionType: 'view' | 'phone_click' | 'map_open' | 'directions') => {
    track('store_interaction', {
      store_name: storeName,
      interaction_type: interactionType,
      store_type: 'physical'
    })
  }

  const trackMapInteraction = (interactionType: 'open' | 'close' | 'directions' | 'phone_click', storeName: string) => {
    track('map_interaction', {
      store_name: storeName,
      interaction_type: interactionType,
      map_type: 'store_location'
    })
  }

  const trackOutOfStock = (productType: string, productColor: string, size: string) => {
    track('out_of_stock', {
      product_type: productType,
      product_color: productColor,
      size,
      stock_status: 'out_of_stock'
    })
  }

  const trackAvailableInStore = (productType: string, productColor: string, size: string, storeName: string) => {
    track('available_in_store', {
      product_type: productType,
      product_color: productColor,
      size,
      store_name: storeName,
      stock_status: 'available_in_store'
    })
  }

  // Booking events
  const trackBookingSuccess = (productType: string, productColor: string, storeName: string, size?: string) => {
    track('booking_success', {
      product_type: productType,
      product_color: productColor,
      store_name: storeName,
      size: size || 'not_selected',
      booking_type: 'try_on_appointment',
      conversion_type: 'booking'
    })
  }

  const trackBookingError = (productType: string, productColor: string, errorType: string) => {
    track('booking_error', {
      product_type: productType,
      product_color: productColor,
      error_type: errorType,
      booking_type: 'try_on_appointment'
    })
  }

  // Other events
  const trackLookbookView = (productType: string, productColor: string) => {
    track('lookbook_view', {
      product_type: productType,
      product_color: productColor,
      interaction_type: 'lookbook'
    })
  }

  // Language change tracking
  const trackLanguageChange = (fromLanguage: string, toLanguage: string, currentPath: string) => {
    track('language_change', {
      from_language: fromLanguage,
      to_language: toLanguage,
      current_path: currentPath,
      interaction_type: 'language_switcher'
    })
  }

  // Lookbook events
  const trackLookbookClick = (productType: string, productColor: string, imageIndex: number) => {
    track('lookbook_click', {
      product_type: productType,
      product_color: productColor,
      image_index: imageIndex,
      interaction_type: 'lookbook_image_click'
    })
  }

  const trackLookbookZoom = (productType: string, productColor: string, isZoomed: boolean) => {
    track('lookbook_zoom', {
      product_type: productType,
      product_color: productColor,
      zoom_state: isZoomed ? 'zoomed_in' : 'zoomed_out',
      interaction_type: 'lookbook_zoom'
    })
  }

  const trackLookbookNavigation = (productType: string, productColor: string, direction: 'prev' | 'next') => {
    track('lookbook_navigation', {
      product_type: productType,
      product_color: productColor,
      direction,
      interaction_type: 'lookbook_navigation'
    })
  }

  // Slider events
  const trackSliderNavigation = (sliderName: string, direction: 'prev' | 'next', slideIndex: number) => {
    track('slider_navigation', {
      slider_name: sliderName,
      direction,
      slide_index: slideIndex,
      interaction_type: 'slider_navigation'
    })
  }

  const trackSliderClick = (sliderName: string, slideIndex: number, slideUrl?: string) => {
    track('slider_click', {
      slider_name: sliderName,
      slide_index: slideIndex,
      slide_url: slideUrl || 'no_url',
      interaction_type: 'slider_click'
    })
  }

  // Map events
  const trackMapGotoStore = (storeName: string, storeAddress: string) => {
    track('map_goto_store', {
      store_name: storeName,
      store_address: storeAddress,
      interaction_type: 'map_directions'
    })
  }

  // Page view tracking
  const trackPageView = (pageType: string, productType?: string, productVariant?: string, language?: string, isAuthenticated?: boolean, userId?: string, emailHash?: string) => {
    const properties: Record<string, any> = {
      page_type: pageType,
      language: language || 'en',
      interaction_type: 'page_view',
      path: typeof window !== 'undefined' ? window.location.pathname : '',
      page_path: typeof window !== 'undefined' ? window.location.pathname : '',
      page_location: typeof window !== 'undefined' ? window.location.href : '',
      page_title: typeof document !== 'undefined' ? document.title : '',
      is_authenticated: isAuthenticated ?? false
    }
    
    if (productType) {
      properties.product_type = productType
    }
    
    if (productVariant) {
      properties.product_variant = productVariant
    }

    // Add user data only if provided (requires explicit consent)
    // RGPD compliant because:
    // 1. Email is hashed (SHA-256) - pseudonymized
    // 2. User ID is Supabase UUID - pseudonymized
    // 3. Only sent with explicit analytics consent
    if (userId) {
      properties.user_id = userId
    }
    if (emailHash) {
      properties.email_hash = emailHash
    }
    
    track('page_view', properties)
  }

  // Country selection tracking
  const trackCountrySelection = (country: 'FR' | 'UK' | 'US', productType?: string, productColor?: string) => {
    track('country_selection', {
      country,
      country_label: country === 'FR' ? 'EU' : country,
      product_type: productType || 'unknown',
      product_color: productColor || 'unknown',
      interaction_type: 'size_guide_country'
    })
  }

  // Product zoom tracking
  const trackProductZoom = (productType: string, productColor: string, isZoomed: boolean) => {
    track('product_zoom', {
      product_type: productType,
      product_color: productColor,
      zoom_state: isZoomed ? 'zoomed_in' : 'zoomed_out',
      interaction_type: 'product_image_zoom'
    })
  }

  // Wishlist tracking
  const trackAddToWishlist = (productId: string, productName: string, price?: number, productType?: string, productColor?: string, isAuthenticated?: boolean) => {
    track('add_to_wishlist', {
      product_id: productId,
      product_name: productName,
      price: price || 0,
      product_type: productType || 'unknown',
      product_color: productColor || 'unknown',
      currency: 'EUR',
      value: price || 0,
      interaction_type: 'wishlist_add',
      is_authenticated: isAuthenticated ?? false
    })
  }

  const trackRemoveFromWishlist = (productId: string, productName: string, productType?: string, productColor?: string, isAuthenticated?: boolean) => {
    track('remove_from_wishlist', {
      product_id: productId,
      product_name: productName,
      product_type: productType || 'unknown',
      product_color: productColor || 'unknown',
      interaction_type: 'wishlist_remove',
      is_authenticated: isAuthenticated ?? false
    })
  }

  // Back to top tracking
  const trackBackToTop = (scrollPosition: number, currentPath: string) => {
    track('back_to_top', {
      scroll_position: scrollPosition,
      current_path: currentPath,
      interaction_type: 'navigation'
    })
  }

  // Authentication tracking (RGPD compliant - no personal data)
  const trackLogin = (method: 'email' | 'google' | 'oauth' = 'email') => {
    track('login', {
      method,
      interaction_type: 'authentication',
      // Note: We don't send email or personal data to respect RGPD
      // Only tracking that a login occurred, not who logged in
    })
  }

  const trackSignUp = (method: 'email' | 'google' | 'oauth' = 'email') => {
    track('sign_up', {
      method,
      interaction_type: 'authentication',
      // Note: We don't send email or personal data to respect RGPD
      // Only tracking that a signup occurred, not who signed up
    })
  }

  // Agent registration (auth.md verified_email claim ceremony)
  // Note: no email or account identifier is sent, only the agent's own
  // self-reported label, to respect RGPD (matches trackLogin/trackSignUp).
  const trackAgentAccessApproved = (clientLabel?: string) => {
    track('agent_access_approved', {
      client_label: clientLabel || 'unlabeled_agent',
      interaction_type: 'agent_auth'
    })
  }

  const trackAgentAccessDenied = (clientLabel?: string) => {
    track('agent_access_denied', {
      client_label: clientLabel || 'unlabeled_agent',
      interaction_type: 'agent_auth'
    })
  }

  return {
    track,
    getAttributionProperties,
    trackEditorialSignal,
    trackFormGoal,
    trackFrictionSignal,
    trackAddToCart,
    trackRemoveFromCart,
    trackBeginCheckout,
    trackCheckoutInformation,
    trackPurchase,
    trackViewItem,
    trackViewCart,
    trackSizeSelection,
    trackColorSelection,
    trackSizeGuideOpen,
    trackProductDetails,
    trackNewsletterSubscribe,
    trackPrivateAccessRequest,
    trackSocialClick,
    trackStoreInteraction,
    trackMapInteraction,
    trackOutOfStock,
    trackAvailableInStore,
    trackBookingSuccess,
    trackBookingError,
    trackLookbookView,
    trackLanguageChange,
    trackLookbookClick,
    trackLookbookZoom,
    trackLookbookNavigation,
    trackSliderNavigation,
    trackSliderClick,
    trackMapGotoStore,
    trackPageView,
    trackCountrySelection,
    trackProductZoom,
    trackAddToWishlist,
    trackRemoveFromWishlist,
    trackBackToTop,
    trackLogin,
    trackSignUp,
    trackAgentAccessApproved,
    trackAgentAccessDenied
  }
}
